import { ChangeEvent, KeyboardEventHandler, MouseEventHandler } from "react";

import { AutocompleteProps as MuiAutocompleteProps, AutocompleteValue } from "@mui/material/Autocomplete";
import { AutocompleteInputChangeReason } from "@mui/material/useAutocomplete";
import { ClassNameMap } from "@mui/styles";
import { FieldValues } from "react-hook-form";

import { BaseFormFieldProps, NonGenericBaseFormFieldProps } from "../../../types/baseFormField";
import { InternalClasses } from "../helpers";
import { BaseTextFieldProps } from "../TextField";

import { DashboardChipShape, DashboardChipVariant, DashboardChipVibe } from "@/dashboard-design-system/DashboardChip";
import { IconElement } from "@/icons/types";
import { InputVibe } from "@/types/mui";

export interface AutocompleteOption<T extends string | number | boolean | unknown = unknown> {
  label: string;
  value: T;
  vibe?: DashboardChipVibe;
}

export interface BaseAutocompleteProps<
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends Pick<BaseTextFieldProps, "classes" | "className" | "disabled" | "placeholder" | "autoFocus" | "name">,
    Omit<NonGenericBaseFormFieldProps, "rules">,
    Omit<MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>, "onChange" | "classes" | "renderInput"> {
  classes?: Partial<ClassNameMap<BaseAutocompleteBaseClasses>>;
  vibe?: InputVibe;
  label?: React.ReactNode | string;
  onInputChange?: (event: ChangeEvent<unknown>, value: string, reason: AutocompleteInputChangeReason) => void;
  inputValue?: string;
  onChange?: (value: AutocompleteValue<Value, Multiple, DisableClearable, FreeSolo> | undefined) => void;
  onClick?: MouseEventHandler<HTMLDivElement> | undefined;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement> | undefined;
  size?: BaseTextFieldProps["size"];
  inputRef?: React.Ref<unknown>;
  openOnFocus?: boolean;
  hideDropdownIcon?: boolean;
  hideClearIcon?: boolean;
  checkbox?: boolean;
  endAdornment?: React.ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
  popupIcon?: IconElement;
  noAsterisk?: boolean;
  chipProps?: { shape: DashboardChipShape; variant: DashboardChipVariant };
}

export interface FormAutocompleteProps<
  T extends FieldValues,
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends BaseFormFieldProps<T>,
    Omit<BaseAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>, "classes" | "name"> {
  classes?: Partial<ClassNameMap<BaseAutocompleteBaseClasses>>;
}

export interface AutocompleteProps<
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  T extends FieldValues = FieldValues,
> extends Omit<BaseAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>, "classes"> {
  classes?: Partial<ClassNameMap<BaseAutocompleteBaseClasses>>;
  control?: BaseFormFieldProps<T>["control"];
  rules?: BaseFormFieldProps<T>["rules"];
  name?: BaseFormFieldProps<T>["name"];
}

export type AutocompleteSsrProps<
  Value,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  T extends FieldValues = FieldValues,
> = AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, T> & {
  forwardedRef?: React.Ref<HTMLInputElement>;
};

export type BaseAutocompleteBaseClasses =
  | "endAdornment"
  | "clearIndicator"
  | "popupIndicator"
  | "disablePopupIndicatorTransform"
  | "indicator"
  | "iconForm"
  | "option"
  | "optionForm"
  | "paper"
  | "input"
  | "inputWithHiddenTags"
  | "listBox"
  | "root"
  | "inputRoot"
  | "inputRootWithHiddenTags";

const PREFIX = "ZeffyAutocomplete";

export const zeffyAutocompleteClasses: InternalClasses<typeof PREFIX, BaseAutocompleteBaseClasses> = {
  endAdornment: `${PREFIX}-endAdornment`,
  clearIndicator: `${PREFIX}-clearIndicator`,
  popupIndicator: `${PREFIX}-popupIndicator`,
  disablePopupIndicatorTransform: `${PREFIX}-disablePopupIndicatorTransform`,
  indicator: `${PREFIX}-indicator`,
  iconForm: `${PREFIX}-iconForm`,
  option: `${PREFIX}-option`,
  optionForm: `${PREFIX}-optionForm`,
  paper: `${PREFIX}-paper`,
  input: `${PREFIX}-input`,
  inputWithHiddenTags: `${PREFIX}-inputWithHiddenTags`,
  inputRoot: `${PREFIX}-inputRoot`,
  inputRootWithHiddenTags: `${PREFIX}-inputRootWithHiddenTags`,
  root: `${PREFIX}-root`,
  listBox: `${PREFIX}-listBox`,
};
